import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  ChevronRight,
  Filter,
  Search,
  Award,
  User,
  Calendar,
  ArrowRight,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { PageHeader, MetricCard } from '@/components/common';
import { rfqApi } from '@/api/services';
import { getErrorMessage } from '@/api/client';

interface PendingApproval {
  id: string;
  requestId: string;
  rfqId: string;
  rfqNumber: string;
  rfqTitle: string;
  rfqType: string;
  shipmentType?: string;
  lineNumber: number;
  originCity: string;
  destCity: string;
  carrierId: string;
  carrierName: string;
  carrierCode: string;
  awardedRate: number;
  levelNumber: number;
  levelName: string;
  requestedAt: string;
  requestedBy?: string;
  status: 'Pending' | 'Approved' | 'Rejected';
}

export default function FreightRFQApprovals() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [approvals, setApprovals] = useState<PendingApproval[]>([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [showRejectModal, setShowRejectModal] = useState<string | null>(null);
  const [rejectComment, setRejectComment] = useState('');

  useEffect(() => {
    loadApprovals();
  }, []);

  const loadApprovals = async () => {
    try {
      setLoading(true);
      const data = await rfqApi.getMyPendingApprovals();
      setApprovals(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load approvals:', error);
      setApprovals([]);
      // Only show toast for non-404 errors (backend may not be implemented yet)
      const errorMessage = getErrorMessage(error);
      if (!errorMessage.includes('404') && !errorMessage.includes('Not Found') && !errorMessage.includes('Cannot GET')) {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId: string) => {
    try {
      setProcessingId(requestId);
      await rfqApi.processApproval(requestId, 'Approve');
      toast.success('Approval processed successfully');
      await loadApprovals();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (requestId: string) => {
    try {
      setProcessingId(requestId);
      await rfqApi.processApproval(requestId, 'Reject', rejectComment);
      toast.success('Rejection processed successfully');
      setShowRejectModal(null);
      setRejectComment('');
      await loadApprovals();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setProcessingId(null);
    }
  };

  // Filter approvals based on tab and search
  const filteredApprovals = approvals.filter((approval) => {
    // Tab filter
    if (activeTab === 'pending' && approval.status !== 'Pending') return false;
    if (activeTab === 'approved' && approval.status !== 'Approved') return false;
    if (activeTab === 'rejected' && approval.status !== 'Rejected') return false;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        approval.rfqNumber.toLowerCase().includes(query) ||
        approval.rfqTitle.toLowerCase().includes(query) ||
        approval.carrierName.toLowerCase().includes(query) ||
        approval.originCity.toLowerCase().includes(query) ||
        approval.destCity.toLowerCase().includes(query)
      );
    }

    return true;
  });

  // Stats
  const pendingCount = approvals.filter((a) => a.status === 'Pending').length;
  const approvedCount = approvals.filter((a) => a.status === 'Approved').length;
  const rejectedCount = approvals.filter((a) => a.status === 'Rejected').length;
  const totalValue = approvals
    .filter((a) => a.status === 'Pending')
    .reduce((sum, a) => sum + a.awardedRate, 0);

  const statusColors: Record<string, string> = {
    Pending: 'bg-amber-100 text-amber-600 border-amber-200',
    Approved: 'bg-emerald-100 text-emerald-600 border-emerald-200',
    Rejected: 'bg-red-100 text-red-600 border-red-200',
  };

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="RFQ Approvals"
        description="Review and approve freight RFQ awards"
      />

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          label="Pending Approvals"
          value={pendingCount}
          icon={<Clock className="w-5 h-5 text-amber-500" />}
        />
        <MetricCard
          label="Approved"
          value={approvedCount}
          icon={<CheckCircle className="w-5 h-5 text-emerald-500" />}
        />
        <MetricCard
          label="Rejected"
          value={rejectedCount}
          icon={<XCircle className="w-5 h-5 text-red-500" />}
        />
        <MetricCard
          label="Pending Value"
          value={`$${totalValue.toLocaleString()}`}
          icon={<DollarSign className="w-5 h-5" />}
        />
      </div>

      {/* Tabs and Search */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="pending">
              Pending
              {pendingCount > 0 && (
                <Badge className="ml-2 bg-amber-100 text-amber-600" variant="secondary">
                  {pendingCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected">Rejected</TabsTrigger>
            <TabsTrigger value="all">All</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search approvals..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-10 w-64"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button variant="outline">
            <Filter className="w-4 h-4 mr-2" />
            Filter
          </Button>
        </div>
      </div>

      {/* Approvals List */}
      <div className="bg-card rounded-xl border">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : filteredApprovals.length > 0 ? (
          <div className="divide-y">
            {filteredApprovals.map((approval) => (
              <div
                key={approval.id}
                className="p-6 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono text-sm text-muted-foreground">
                        {approval.rfqNumber}
                      </span>
                      <Badge
                        variant="outline"
                        className={statusColors[approval.status]}
                      >
                        {approval.status}
                      </Badge>
                      <Badge variant="outline">{approval.rfqType}</Badge>
                      {approval.shipmentType && (
                        <Badge
                          variant="outline"
                          className="bg-blue-50 text-blue-600 border-blue-200"
                        >
                          {approval.shipmentType.replace('_', ' ')}
                        </Badge>
                      )}
                    </div>

                    {/* Title */}
                    <h3 className="text-lg font-semibold mb-2">
                      {approval.rfqTitle}
                    </h3>

                    {/* Lane Info */}
                    <div className="flex items-center gap-6 text-sm text-muted-foreground mb-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          Lane #{approval.lineNumber}:
                        </span>
                        <span>{approval.originCity}</span>
                        <ArrowRight className="w-4 h-4" />
                        <span>{approval.destCity}</span>
                      </div>
                    </div>

                    {/* Award Details */}
                    <div className="flex items-center gap-6 text-sm">
                      <div className="flex items-center gap-2">
                        <Award className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">
                          Awarded to:
                        </span>
                        <span className="font-medium">{approval.carrierName}</span>
                        <span className="text-muted-foreground">
                          ({approval.carrierCode})
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Rate:</span>
                        <span className="font-bold">
                          ${approval.awardedRate.toLocaleString()}
                        </span>
                      </div>
                    </div>

                    {/* Meta */}
                    <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <User className="w-3.5 h-3.5" />
                        <span>
                          Level {approval.levelNumber}: {approval.levelName}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        <span>
                          Requested{' '}
                          {new Date(approval.requestedAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 ml-6">
                    {approval.status === 'Pending' && (
                      <>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => setShowRejectModal(approval.requestId)}
                          disabled={processingId === approval.requestId}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleApprove(approval.requestId)}
                          disabled={processingId === approval.requestId}
                        >
                          {processingId === approval.requestId ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve
                            </>
                          )}
                        </Button>
                      </>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/tms/rfq/${approval.rfqId}`)}
                    >
                      View RFQ
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <CheckCircle className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {activeTab === 'pending'
                ? 'No Pending Approvals'
                : 'No Approvals Found'}
            </h3>
            <p className="text-muted-foreground">
              {activeTab === 'pending'
                ? 'You have no pending RFQ awards to approve.'
                : 'No approvals match your search criteria.'}
            </p>
          </div>
        )}
      </div>

      {/* Reject Modal */}
      <Dialog
        open={!!showRejectModal}
        onOpenChange={(open) => {
          if (!open) {
            setShowRejectModal(null);
            setRejectComment('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
                <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              Reject Approval
            </DialogTitle>
            <DialogDescription>
              Please provide a reason for rejection
            </DialogDescription>
          </DialogHeader>

          <Textarea
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
            placeholder="Enter rejection reason..."
            rows={4}
            className="resize-none"
          />

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRejectModal(null);
                setRejectComment('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => showRejectModal && handleReject(showRejectModal)}
              disabled={
                !rejectComment.trim() || processingId === showRejectModal
              }
            >
              {processingId === showRejectModal ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                'Confirm Rejection'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
